import { takeEvery, put, call, select } from 'redux-saga/effects';
import { push } from 'redux-first-history';
import * as types from '../constants/ActionTypes';
import {
  INSUFFICIENT_FUNDS,
  NO_REQUISITES,
  PAYOUT_IN_PROCESS,
  PAYOUT_SUCCESS_MESSAGE,
} from '../constants/NetworkErrors';
import formQuery from '../functions/URL/formQuery';
import Api from '../functions/Api';

export const REPORTS_PAGE_SIZE = 10;

// `type` of the problems the payout request answers with (see docs/dashboard.md in the backend)
export const PAYOUT_PROBLEMS = {
  '/problems/payout-in-progress': PAYOUT_IN_PROCESS,
  '/problems/no-requisites': NO_REQUISITES,
  '/problems/nothing-to-pay-out': INSUFFICIENT_FUNDS,
};

/**
 * Money summary of a role: pending, accrued, paid out, available, last week and month
 * @param {Object} $
 * @param {String} $.role - `'author'`, `'affiliate'` or `'all'` for both together
 */
export function* fetchReportsSummary({ role }) {
  try {
    const data = yield call(
      Api.get,
      `v1/dashboard/reports/summary/${formQuery({ role })}`
    );
    yield put({ type: types.FETCH_REPORTS_SUMMARY_SUCCESS, role, data });
  } catch (error) {
    yield put({ type: types.FETCH_REPORTS_SUMMARY_FAILURE, role, error });
    yield put({ type: types.NETWORK_ERROR, error });
  }
}

/**
 * One page of the sales table: a row per sale, as author and as affiliate
 * @param {Object} $
 * @param {Number} [$.page=1] - number of requested page
 * @param {Object} [$.query] - the filters of the table, as the API takes them
 */
export function* fetchReportsSales({ page = 1, query = {} }) {
  try {
    const data = yield call(
      Api.get,
      `v1/dashboard/reports/sales/${formQuery({
        ...query,
        page,
        page_size: REPORTS_PAGE_SIZE,
      })}`
    );
    yield put({
      type: types.FETCH_REPORTS_SALES_SUCCESS,
      page,
      pageSize: REPORTS_PAGE_SIZE,
      data,
    });
  } catch (error) {
    yield put({ type: types.FETCH_REPORTS_SALES_FAILURE, error });
    yield put({ type: types.NETWORK_ERROR, error });
  }
}

/**
 * One page of the user's payouts with the balance block
 * @param {Object} $
 * @param {Number} [$.page=1] - number of requested page
 */
export function* fetchReportsPayouts({ page = 1 }) {
  try {
    const data = yield call(
      Api.get,
      `v1/dashboard/reports/payouts/${formQuery({
        page,
        page_size: REPORTS_PAGE_SIZE,
      })}`
    );
    yield put({
      type: types.FETCH_PAYOUTS_SUCCESS,
      page,
      pageSize: REPORTS_PAGE_SIZE,
      data,
    });
  } catch (error) {
    yield put({ type: types.FETCH_PAYOUTS_FAILURE, error });
    yield put({ type: types.NETWORK_ERROR, error });
  }
}

const isFirstNotPayoutRequest =
  localStorage.getItem('isFirstPayoutRequest') === 'false';

/**
 * Ask for a payout of the whole available balance; the server says why it cannot be done
 */
export function* requestPayout() {
  try {
    yield call(Api.post, 'v1/dashboard/reports/payouts/');
    if (isFirstNotPayoutRequest === true) {
      yield put({
        type: types.CUSTOM_ERROR,
        error: PAYOUT_SUCCESS_MESSAGE,
      });
    }
    yield put({ type: types.REQUEST_PAYOUT_SUCCESS });
  } catch (error) {
    const problem =
      PAYOUT_PROBLEMS[error && error.response && error.response.type];
    if (!problem) {
      yield put({ type: types.NETWORK_ERROR, error });
      return;
    }
    yield put({ type: types.CUSTOM_ERROR, error: problem });
    if (problem === PAYOUT_IN_PROCESS) {
      localStorage.setItem('isFirstPayoutRequest', 'false');
    }
    if (problem === NO_REQUISITES) {
      yield put(push('/settings/requisites?fromRedirectPayouts=true'));
    }
  }
}

/**
 * Re-read the first page after a payout was requested, so it shows in the list
 */
export function* updatePayoutsAfterRequest() {
  const current = yield select((state) => state.reports.payouts.current);
  yield put({ type: types.FETCH_PAYOUTS, page: current || 1 });
}

export default function* watchReports() {
  yield takeEvery(types.FETCH_REPORTS_SUMMARY, fetchReportsSummary);
  yield takeEvery(types.FETCH_REPORTS_SALES, fetchReportsSales);
  yield takeEvery(types.FETCH_PAYOUTS, fetchReportsPayouts);
  yield takeEvery(types.REQUEST_PAYOUT, requestPayout);
  yield takeEvery(types.REQUEST_PAYOUT_SUCCESS, updatePayoutsAfterRequest);
}
